import React from 'react';
import { applyTimeOfDayMs } from '@/lib/schedule-datetime';
import { DateTimeRow } from './DateTimeRow';
import { FormRow } from './FormRow';

export type DateFormEntry = {
  startTime?: string | null;
  endTime?: string | null;
  startTimeMS?: number | null;
  endTimeMS?: number | null;
  /** YYYY-MM-DD when a calendar day is valid before start/end times exist. */
  selectedDate?: string | null;
  /** Typed MM/DD/YYYY mask text (may be incomplete or invalid). */
  dateInput?: string | null;
};

type DateFormWidgetProps = {
  dates?: DateFormEntry[] | null;
  label?: string;
  allowMultiple?: boolean;
  required?: boolean;
  handleDateChange: (dates: DateFormEntry[]) => void;
  readOnly?: boolean;
  datesError?: string | null;
  layout?: 'inline' | 'stacked';
};

const defaultDateObject: DateFormEntry = {
  startTime: null,
  endTime: null,
  startTimeMS: null,
  endTimeMS: null,
  selectedDate: null,
  dateInput: null,
};

export function DateFormWidget({
  dates: datesProp,
  label,
  allowMultiple,
  handleDateChange,
  readOnly = false,
  datesError,
  layout = 'stacked',
}: DateFormWidgetProps) {
  let dates = datesProp;
  if (!dates || !dates.length) {
    dates = [defaultDateObject];
  }

  const addDateRow = () => {
    const newDateRow = { ...defaultDateObject };
    handleDateChange([...dates, newDateRow]);
  };

  const deleteDateRow = (index: number) => {
    const next = [...dates];
    next.splice(index, 1);
    handleDateChange(next);
  };

  const dateChanged = (index: number, ms: number | null) => {
    const next = [...dates];
    const current = next[index];
    let endTimeMS = current.endTimeMS ?? null;
    // Keep end clock time on the same calendar day as the new start date.
    if (ms != null && endTimeMS != null && endTimeMS > 0) {
      endTimeMS = applyTimeOfDayMs(ms, endTimeMS);
    }
    next[index] = {
      ...current,
      startTimeMS: ms != null ? ms : null,
      endTimeMS,
      // Null (don't delete) so parent spreads clear stale ISO/display strings.
      startTime: null,
      endTime: null,
      selectedDate: ms != null ? null : current.selectedDate,
      dateInput: ms != null ? null : current.dateInput,
    };
    handleDateChange(next);
  };

  const dateInputChanged = (
    index: number,
    dateInput: string | null,
    isoDate: string | null,
  ) => {
    const next = [...dates];
    next[index] = {
      ...next[index],
      dateInput: dateInput || null,
      selectedDate: isoDate,
    };
    handleDateChange(next);
  };

  const startTimeChanged = (index: number, ms: number | null) => {
    const next = [...dates];
    next[index] = {
      ...next[index],
      startTimeMS: ms != null ? ms : null,
      startTime: null,
      selectedDate: ms != null ? null : next[index].selectedDate,
      dateInput: ms != null ? null : next[index].dateInput,
    };
    handleDateChange(next);
  };

  const endTimeChanged = (index: number, ms: number | null) => {
    const next = [...dates];
    next[index] = {
      ...next[index],
      endTimeMS: ms != null ? ms : null,
      // Explicit null clears stale display/ISO values on the parent object.
      endTime: null,
    };
    handleDateChange(next);
  };

  return (
    <div data-form-field="dates">
      {dates.map((date, index) => (
        <React.Fragment key={`date-row-${index}`}>
          {index > 0 ? <hr className="festy-datetime-day-separator" /> : null}
          <FormRow
            layout={layout}
            label={`${index === 0 ? label : ''}`}
            error={index === 0 && datesError ? datesError : undefined}
          >
            <DateTimeRow
              index={index}
              date={date.startTimeMS}
              startTime={date.startTimeMS}
              endTime={date.endTimeMS}
              selectedDate={date.selectedDate}
              dateInput={date.dateInput}
              onRowAdd={addDateRow}
              onRowDelete={deleteDateRow}
              onDateChange={dateChanged}
              onDateInputChange={dateInputChanged}
              onStartTimeChange={startTimeChanged}
              onEndTimeChange={endTimeChanged}
              allowMultiple={allowMultiple}
              readOnly={readOnly}
            />
          </FormRow>
        </React.Fragment>
      ))}
    </div>
  );
}
